/**
 * TEAM SETTINGS TAB
 *
 * Team member management: invite, list, change roles, remove.
 * Permission-aware: owners/admins see invite form and action buttons.
 */

import { useState, useEffect } from "react";
import { UserPlus, X, Crown, Shield, UserCheck, Eye, AlertCircle, CheckCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTeamStore, type MemberRole } from "@/stores/teamStore";

const ROLE_META: Record<MemberRole, { label: string; icon: typeof Crown; color: string }> = {
  owner:  { label: "Owner",  icon: Crown,     color: "text-amber-400" },
  admin:  { label: "Admin",  icon: Shield,    color: "text-blue-400" },
  member: { label: "Member", icon: UserCheck, color: "text-green-400" },
  viewer: { label: "Viewer", icon: Eye,       color: "text-muted-foreground" },
};

const AVATAR_COLORS = [
  "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  "bg-green-500/15 text-green-600 dark:text-green-400",
  "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",
  "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400",
  "bg-orange-500/15 text-orange-600 dark:text-orange-400",
];

function getAvatarColor(name: string | null, email: string | null): string {
  const source = name || email || "?";
  let hash = 0;
  for (let i = 0; i < source.length; i++) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function canEditRole(myRole: MemberRole | null, targetRole: MemberRole): boolean {
  if (myRole === "owner") return targetRole !== "owner";
  if (myRole === "admin") return targetRole === "member" || targetRole === "viewer";
  return false;
}

function canInvite(myRole: MemberRole | null): boolean {
  return myRole === "owner" || myRole === "admin";
}

function getInitials(name: string | null, email: string | null): string {
  const source = name || email || "?";
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}

export function TeamSettingsTab({ workspaceId }: { workspaceId: number }) {
  const members = useTeamStore((s) => s.members);
  const currentUserRole = useTeamStore((s) => s.currentUserRole);
  const isLoading = useTeamStore((s) => s.isLoading);
  const error = useTeamStore((s) => s.error);
  const fetchMembers = useTeamStore((s) => s.fetchMembers);
  const inviteMember = useTeamStore((s) => s.inviteMember);
  const updateMemberRole = useTeamStore((s) => s.updateMemberRole);
  const removeMember = useTeamStore((s) => s.removeMember);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("member");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchMembers(workspaceId);
  }, [workspaceId, fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteError(null);
    setInviteSuccess(false);
    setInviting(true);
    try {
      await inviteMember(workspaceId, inviteEmail.trim(), inviteRole);
      setInviteEmail("");
      setInviteRole("member");
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
    } catch (e: any) {
      setInviteError(e.message || "Failed to invite member");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId: number, role: MemberRole) => {
    try {
      await updateMemberRole(workspaceId, memberId, role);
    } catch (e: any) {
      console.error("Failed to update role:", e);
    }
  };

  const handleRemove = async (memberId: number, isSelf: boolean) => {
    const msg = isSelf
      ? "Are you sure you want to leave this workspace?"
      : "Are you sure you want to remove this member?";
    if (!window.confirm(msg)) return;
    try {
      await removeMember(workspaceId, memberId);
    } catch (e: any) {
      console.error("Failed to remove member:", e);
    }
  };

  if (isLoading && members.length === 0) {
    return <div className="py-12 text-center text-muted-foreground text-sm">Loading team members...</div>;
  }

  if (error && members.length === 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 px-4 py-3">
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load team</p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite section */}
      {canInvite(currentUserRole) && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Invite a team member</h3>
          <div className="flex gap-2 rounded-lg border border-border p-2 bg-muted/30">
            <Input
              placeholder="Email address"
              type="email"
              aria-label="Invite email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              className="flex-1 h-8 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60"
            />
            <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as MemberRole)}>
              <SelectTrigger className="w-[100px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 gap-1.5 px-3" onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
              <UserPlus className="w-3.5 h-3.5" />
              Invite
            </Button>
          </div>

          {inviteError && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 dark:bg-red-950/20 px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600 dark:text-red-400">{inviteError}</p>
            </div>
          )}
          {inviteSuccess && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 dark:bg-green-950/20 px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
              <p className="text-xs text-green-600 dark:text-green-400">Invitation sent successfully</p>
            </div>
          )}
        </div>
      )}

      {/* Members list */}
      <div>
        <h3 className="text-sm font-medium mb-3">Members ({members.length})</h3>

        {members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mb-1">No team members yet</p>
            {canInvite(currentUserRole) && (
              <p className="text-xs text-muted-foreground">Use the form above to invite your first team member.</p>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-border divide-y divide-border">
            {members.map((member) => {
              const meta = ROLE_META[member.role];
              const RoleIcon = meta.icon;
              const isPending = member.invitation_status === "pending";
              const displayName = isPending ? (member.email || "Pending invite") : (member.name || member.email || "Unknown");

              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 py-2.5 px-3 group hover:bg-muted/30 transition-colors first:rounded-t-lg last:rounded-b-lg"
                >
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${getAvatarColor(member.name, member.email)}`}>
                    {getInitials(member.name, member.email)}
                  </div>

                  {/* Name + email */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{displayName}</span>
                      {isPending && (
                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-amber-500/10 text-amber-500 border-0 hover:bg-amber-500/10">
                          Pending
                        </Badge>
                      )}
                      {member.is_self && (
                        <span className="text-[10px] text-muted-foreground">(you)</span>
                      )}
                    </div>
                    {!isPending && member.email && member.name && (
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    )}
                  </div>

                  {/* Role badge or selector */}
                  {canEditRole(currentUserRole, member.role) && !member.is_self ? (
                    <Select value={member.role} onValueChange={(v) => handleRoleChange(member.id, v as MemberRole)}>
                      <SelectTrigger className="w-[100px] h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentUserRole === "owner" && <SelectItem value="admin">Admin</SelectItem>}
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className={`flex items-center gap-1 text-xs ${meta.color}`}>
                      <RoleIcon className="w-3 h-3" />
                      {meta.label}
                    </div>
                  )}

                  {/* Remove / Leave button */}
                  {member.role !== "owner" && (canEditRole(currentUserRole, member.role) || member.is_self) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(member.id, member.is_self)}
                      aria-label={member.is_self ? "Leave workspace" : "Remove member"}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
